import * as THREE from 'three';
import type { Person } from './population';
import type { Naval, NavalShip } from './naval';
import type { NavalLanding } from '../world/strategic-transport';

interface Passenger {
  person: Person;
  ship: NavalShip;
  seat: number;
  state: 'aboard' | 'boarding' | 'leaving' | 'landed' | 'casualty';
  path: THREE.Vector3[];
  detour?: THREE.Vector3[];
  blockedFor?: number;
  nextBypass?: number;
}
interface Landing {
  layout: NavalLanding;
  queue: Passenger[];
  next: number;
}

/** Real seated originals walk along the deck aisle and supported gangway.
 * No replacement shore actors are created when a landing finishes. */
export class NavalPassengers {
  readonly people = new Map<string, Passenger>();
  bypasses = 0;
  private landings = new Map<NavalShip, Landing>();
  private merges = new Map<NavalShip, string>();
  onLand: (person: Person, ship: NavalShip) => void = () => {};
  onBoard: (person: Person, ship: NavalShip) => void = () => {};
  constructor(readonly naval: Naval) {}
  clear(): void {
    this.people.clear();
    this.landings.clear();
    this.merges.clear();
    this.bypasses = 0;
  }
  attach(ship: NavalShip, person: Person, seat: number): boolean {
    if (
      seat < 0 ||
      seat >= 100 ||
      this.people.has(person.id) ||
      person.carrier ||
      person.fatal ||
      ['incapacitated', 'falling'].includes(person.state) ||
      [...this.people.values()].some((p) => p.ship === ship && p.seat === seat && p.state !== 'landed') ||
      person.p.distanceTo(this.naval.seat(ship, seat)) > 0.05
    )
      return false;
    person.carrier = ship.boat.id;
    person.exposedSeat = true;
    person.controlled = true;
    person.hidden = false;
    person.state = 'sitting';
    person.seatHeight = 0.42;
    this.people.set(person.id, { person, ship, seat, state: 'aboard', path: [] });
    return true;
  }
  crossingGangway(ship: NavalShip): boolean {
    return [...this.people.values()].some(
      (p) => p.ship === ship && ['leaving', 'boarding'].includes(p.state),
    );
  }
  cancelLanding(ship: NavalShip): void {
    const landing = this.landings.get(ship);
    if (landing) landing.queue = [];
  }
  syncSeats(): void {
    for (const p of this.people.values())
      if (p.state === 'aboard') {
        p.person.previous.copy(p.person.p);
        p.person.p.copy(this.naval.seat(p.ship, p.seat));
        p.person.yaw = p.ship.boat.yaw;
      }
  }
  private berthed(ship: NavalShip, layout: NavalLanding): boolean {
    const boat = ship.boat,
      physicalDoor = this.naval.deckPoint(ship, -6.05, 0),
      support = this.naval.physics.ground(layout.door, 0.2, 0.3);
    return (
      ship.state === 'moored' &&
      boat.state !== 'wreck' &&
      boat.actualSpeed <= 0.05 &&
      boat.p.distanceTo(new THREE.Vector3(layout.berth.x, layout.berth.y, layout.berth.z)) <= 0.4 &&
      physicalDoor.distanceTo(new THREE.Vector3(layout.door.x, layout.door.y, layout.door.z)) <= 0.3 &&
      !!support
    );
  }
  board(ship: NavalShip, person: Person, seat: number, layout: NavalLanding): boolean {
    const original = this.people.get(person.id),
      centre = layout.path.at(-2)!;
    if (
      seat < 0 ||
      seat >= 100 ||
      !Number.isInteger(seat) ||
      !this.berthed(ship, layout) ||
      person.carrier ||
      person.fatal ||
      ['incapacitated', 'falling', 'carried', 'recovering'].includes(person.state) ||
      (original && !['landed', 'casualty'].includes(original.state)) ||
      this.landings.has(ship) ||
      [...this.people.values()].some((p) => p.ship === ship && p.state === 'leaving') ||
      [...this.people.values()].filter((p) => p.ship === ship && p.state === 'boarding').length >= 4 ||
      [...this.people.values()].some(
        (p) => p.ship === ship && p.seat === seat && !['landed', 'casualty'].includes(p.state),
      ) ||
      person.p.distanceTo(new THREE.Vector3(centre.x, centre.y, centre.z)) > 0.65 ||
      !this.naval.physics.ground(person.p, 0.12, 0.3)
    )
      return false;
    const local = this.naval.seat(ship, seat).sub(ship.boat.p).applyQuaternion(ship.boat.q.clone().invert()),
      front = local.z - 0.7;
    const passenger: Passenger = {
      person,
      ship,
      seat,
      state: 'boarding',
      path: [
        this.naval.deckPoint(ship, -6.05, 0),
        this.naval.deckPoint(ship, -5.1, 0),
        this.naval.deckPoint(ship, -0.3, 0),
        this.naval.deckPoint(ship, -0.3, front),
        this.naval.deckPoint(ship, local.x, front),
        this.naval.seat(ship, seat),
      ],
    };
    this.naval.population.civil?.cancelTask(person, 'Boarding the original naval transport');
    person.carrier = ship.boat.id;
    person.exposedSeat = person.controlled = true;
    person.hidden = false;
    person.state = 'walk';
    person.seatHeight = person.seatBlend = undefined;
    this.people.set(person.id, passenger);
    return true;
  }
  disembark(ship: NavalShip, layout: NavalLanding, retain: ReadonlySet<string> = new Set()): boolean {
    if (this.landings.has(ship)) return true;
    if (
      !this.berthed(ship, layout) ||
      [...this.people.values()].some((p) => p.ship === ship && p.state === 'boarding')
    )
      return false;
    const queue = [...this.people.values()].filter(
      (p) => p.ship === ship && p.state === 'aboard' && !retain.has(p.person.id),
    );
    const aisleDistance = (seat: number) => Math.abs((seat % 8) - 3.5);
    // The left seats beside the opening exit directly. Clear the outermost
    // seat first: releasing it behind its inward neighbours made it step into
    // their cross-deck stream, leaving both directions blocked at the corner.
    const exitOrder = (seat: number) =>
      Math.abs(-15 + Math.floor(seat / 8) * 1.75 - 0.7) < 0.2 ? seat % 8 : aisleDistance(seat);
    queue.sort(
      (a, b) =>
        Math.abs(-15 + Math.floor(a.seat / 8) * 1.75) - Math.abs(-15 + Math.floor(b.seat / 8) * 1.75) ||
        exitOrder(a.seat) - exitOrder(b.seat),
    );
    this.landings.set(ship, { layout, queue, next: this.naval.time });
    ship.reason = 'Original passengers unloading through the actual deck aisle and gangway';
    return true;
  }
  private release(p: Passenger): void {
    const person = p.person;
    p.state = 'casualty';
    if (this.merges.get(p.ship) === person.id) this.merges.delete(p.ship);
    p.path = [];
    p.detour = undefined;
    person.carrier = undefined;
    person.seatHeight = person.seatBlend = undefined;
    person.exposedSeat = false;
    person.controlled = false;
    person.hidden = false;
    if (!['incapacitated', 'falling'].includes(person.state))
      this.naval.population.incapacitate(person, p.ship.boat.p, 1.8);
    person.velocity.add(p.ship.boat.body.linvel());
  }
  private blocked(p: Passenger, dt: number): void {
    p.person.state = 'wait';
    p.blockedFor = (p.blockedFor ?? 0) + dt;
    if (p.blockedFor < 0.8 || this.naval.time < (p.nextBypass ?? 0)) return;
    p.nextBypass = this.naval.time + 2;
    if (p.detour?.length) {
      if (p.blockedFor > 5) p.detour = undefined;
      return;
    }
    const origin = p.person.p,
      goal = p.path[0];
    if (!goal) return;
    // A casualty can occupy an old aisle waypoint. Rejoin the next segment
    // only after proving the detour's ground, hull and other-person clearance.
    const skip = origin.distanceTo(goal) < 1.5 && p.path.length > 1,
      target = skip ? p.path[1] : goal,
      forward = target.clone().sub(origin).setY(0),
      distance = forward.length();
    if (distance < 0.05) return;
    forward.divideScalar(distance);
    const side = new THREE.Vector3(-forward.z, 0, forward.x),
      advance = Math.min(1.6, distance);
    for (const width of [0.72, 1.1])
      for (const sign of p.seat % 2 ? [1, -1] : [-1, 1]) {
        const a = origin.clone().addScaledVector(side, sign * width),
          b = a.clone().addScaledVector(forward, advance),
          join = origin.clone().addScaledVector(forward, advance),
          detour = [a, b, join];
        let from = origin.clone(),
          clear = true;
        for (const to of detour) {
          const move = to.clone().sub(from),
            steps = Math.max(1, Math.ceil(move.length() / 0.2));
          let prior = from.clone();
          for (let i = 1; i <= steps; i++) {
            const sample = from.clone().addScaledVector(move, i / steps),
              support =
                this.naval.deckSupport(p.ship, sample) ?? this.naval.physics.ground(sample, 0.18, 0.3)?.y;
            if (support === undefined || Math.abs(support - prior.y) > 0.2) {
              clear = false;
              break;
            }
            sample.y = support;
            if (!this.naval.population.footClear(p.person, sample, prior)) {
              clear = false;
              break;
            }
            prior = sample;
          }
          if (!clear || this.naval.physics.sweepFoot(from, prior.clone().sub(from)) < 0.999) {
            clear = false;
            break;
          }
          to.copy(prior);
          from = to;
        }
        if (!clear) continue;
        if (skip) p.path.shift();
        p.detour = detour;
        p.blockedFor = 0;
        this.bypasses++;
        return;
      }
  }
  update(dt: number): void {
    for (const p of this.people.values()) {
      const person = p.person,
        boat = p.ship.boat;
      if (p.state === 'landed' || p.state === 'casualty') continue;
      if (boat.state === 'wreck' || person.fatal || ['incapacitated', 'falling'].includes(person.state)) {
        this.release(p);
        continue;
      }
      if (p.state === 'aboard') {
        continue;
      }
      if (
        this.naval.deckSupport(p.ship, person.p) === undefined &&
        !this.naval.physics.ground(person.p, 0.12, 0.3)
      ) {
        p.state = 'casualty';
        if (this.merges.get(p.ship) === person.id) this.merges.delete(p.ship);
        p.path = [];
        person.carrier = undefined;
        person.exposedSeat = false;
        person.controlled = false;
        person.state = 'falling';
        person.fallStart = person.p.y;
        person.velocity.set(0, 0, 0);
        continue;
      }
      const goal = p.detour?.[0] ?? p.path[0];
      if (!goal) {
        if (this.merges.get(p.ship) === person.id) this.merges.delete(p.ship);
        if (p.state === 'boarding') {
          p.state = 'aboard';
          person.state = 'sitting';
          person.seatHeight = 0.42;
          this.onBoard(person, p.ship);
          continue;
        }
        p.state = 'landed';
        person.carrier = undefined;
        person.exposedSeat = false;
        person.layer = 'street';
        this.onLand(person, p.ship);
        continue;
      }
      const local = person.p.clone().sub(boat.p).applyQuaternion(boat.q.clone().invert());
      if (local.x < -1.1 && this.merges.get(p.ship) === person.id) this.merges.delete(p.ship);
      const localGoal = goal.clone().sub(boat.p).applyQuaternion(boat.q.clone().invert());
      if (
        p.state === 'leaving' &&
        local.x >= -1.1 &&
        Math.abs(localGoal.z) < 0.6 &&
        localGoal.x <= 0.35 &&
        Math.abs(local.z - localGoal.z) < 1.25
      ) {
        const owner = this.merges.get(p.ship);
        if (owner && owner !== person.id) {
          person.state = 'wait';
          continue;
        }
        this.merges.set(p.ship, person.id);
      }
      if (person.p.distanceTo(goal) < 0.025) {
        if (p.detour?.length) {
          p.detour.shift();
          if (!p.detour.length) p.detour = undefined;
        } else p.path.shift();
        p.blockedFor = 0;
        continue;
      }
      const direction = goal.clone().sub(person.p).setY(0),
        span = direction.length();
      const next = person.p.clone().addScaledVector(direction.normalize(), Math.min(span, dt * person.speed));
      const ownDeck = this.naval.deckSupport(p.ship, next),
        ground = ownDeck ?? this.naval.physics.ground(next, 0.18, 0.3)?.y;
      if (ground === undefined || Math.abs(ground - person.p.y) > 0.2) {
        this.blocked(p, dt);
        continue;
      }
      next.y = ground;
      if (!this.naval.population.footClear(person, next)) {
        this.blocked(p, dt);
        continue;
      }
      const move = next.clone().sub(person.p),
        allowed = this.naval.physics.sweepFoot(person.p, move);
      person.previous.copy(person.p);
      if (allowed > 0.01) {
        person.p.addScaledVector(move, allowed);
        person.yaw = Math.atan2(-direction.x, -direction.z);
        person.state = 'walk';
        p.blockedFor = 0;
      } else this.blocked(p, dt);
    }
    for (const [ship, landing] of this.landings) {
      if (ship.boat.state === 'wreck') {
        this.landings.delete(ship);
        continue;
      }
      const active = [...this.people.values()].filter((p) => p.ship === ship && p.state === 'leaving').length;
      if (active >= 8 || this.naval.time < landing.next) continue;
      const p = landing.queue.shift();
      if (!p) {
        if (!active) this.landings.delete(ship);
        continue;
      }
      if (p.state !== 'aboard') continue;
      const seat = this.naval
          .seat(ship, p.seat)
          .sub(ship.boat.p)
          .applyQuaternion(ship.boat.q.clone().invert()),
        centre = landing.layout.path.at(-2)!,
        front = seat.z - 0.7,
        exit = Math.abs(front) < 0.2 ? front : front < 0 ? -0.5 : 0.5,
        aisle = seat.x < 0 ? -0.3 : 0.3;
      // Separate the two central walking lanes. The row immediately beside
      // the opening walks directly out rather than first walking to the
      // centre and reversing through the same approaching row.
      p.path = [this.naval.deckPoint(ship, seat.x, front)];
      if (!(seat.x < 0 && Math.abs(front) < 0.2))
        p.path.push(this.naval.deckPoint(ship, aisle, front), this.naval.deckPoint(ship, aisle, exit));
      p.path.push(
        this.naval.deckPoint(ship, -5.1, exit),
        this.naval.deckPoint(ship, -6.05, exit),
        new THREE.Vector3(centre.x, centre.y, centre.z + exit),
      );
      p.state = 'leaving';
      p.person.seatHeight = p.person.seatBlend = undefined;
      p.person.state = 'walk';
      landing.next = this.naval.time + 0.4;
    }
  }
  inspect(ship: NavalShip): unknown {
    return [...this.people.values()]
      .filter((p) => p.ship === ship)
      .map((p) => ({
        id: p.person.id,
        seat: p.seat,
        state: p.state,
        position: p.person.p.toArray(),
        next: p.path[0]?.toArray(),
        detour: p.detour?.map((point) => point.toArray()),
        blockedSeconds: p.blockedFor ?? 0,
      }));
  }
  forget(ship: NavalShip): void {
    for (const [id, p] of this.people) if (p.ship === ship) this.people.delete(id);
    this.landings.delete(ship);
    this.merges.delete(ship);
  }
}
